/**
 * The export preview: the first few records exactly as the file lays them
 * out (built by the SAME engine code the export runs — `POST …/preview`),
 * drawn like a spreadsheet: column letters, row numbers, the group heading
 * row, header columns tinted blue and line columns green, one tab per sheet.
 */
import { useState } from 'react'
import type { CSSProperties } from 'react'
import { Loader2 } from 'lucide-react'
import { Note, meta, mono } from '../../messaging/ui'
import { tw } from '../../testHooks'
import type { ExportPreview } from './types'

const GRID = 'var(--color-border, #dfe4ea)'
const TINT_H = 'rgba(0, 97, 208, 0.06)'
const TINT_D = 'rgba(23, 112, 63, 0.07)'

function letter(i: number): string {
  let s = ''
  let n = i + 1
  while (n) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26) }
  return s
}

const cell: CSSProperties = {
  border: `1px solid ${GRID}`, padding: '3px 8px', whiteSpace: 'nowrap', height: 24, fontSize: 13,
  fontVariantNumeric: 'tabular-nums', color: 'var(--color-text, #1b2330)',
}
const headCell: CSSProperties = {
  ...cell, background: 'var(--color-surface-2, #f4f6f9)', color: 'var(--color-text-faint, #687281)',
  fontWeight: 500, textAlign: 'center', position: 'sticky', top: 0, zIndex: 1, minWidth: 44,
}

export function SheetPreview({ preview, busy, error, format, emptyText }: {
  preview: ExportPreview | null
  busy?: boolean
  error?: string | null
  format?: string
  emptyText?: string
}) {
  const [active, setActive] = useState(0)
  if (error) return <Note tone="error">{error}</Note>
  if (!preview) {
    return (
      <div style={{ ...meta, display: 'flex', alignItems: 'center', gap: 8, padding: 12 }}>
        <Loader2 size={14} className="spin" /> Building the preview…
      </div>
    )
  }
  if (format === 'json' && preview.json) {
    return (
      <div>
        <pre style={{ ...mono, margin: 0, padding: 12, maxHeight: 360, overflow: 'auto', borderRadius: 6,
          background: 'var(--color-surface-2, #f0f3f7)', border: `1px solid ${GRID}` }}>
          {JSON.stringify(preview.json, null, 2)}
        </pre>
        <p style={{ ...meta, margin: '6px 0 0' }}>JSON keeps each record's lines inside it, whatever the sheet layout.</p>
      </div>
    )
  }
  const sheets = preview.sheets
  if (!sheets.length || preview.records === 0) {
    return <Note>{emptyText || 'No records to show. The preview uses the first few records of this screen.'}</Note>
  }
  const idx = Math.min(active, sheets.length - 1)
  const sh = sheets[idx]
  const tint = (part: string) => (part === 'd' ? TINT_D : TINT_H)
  return (
    <div style={{ opacity: busy ? 0.6 : 1, transition: 'opacity .15s' }} {...tw('group', 'export-preview')}>
      <div style={{ overflow: 'auto', maxHeight: 360, border: `1px solid ${GRID}`, borderRadius: '6px 6px 0 0',
        background: 'var(--color-bg, #fff)' }}>
        <table style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={headCell} aria-hidden="true" />
              {sh.headings.map((_, i) => <th key={i} style={headCell}>{letter(i)}</th>)}
            </tr>
          </thead>
          <tbody>
            {sh.groups.length > 0 && (
              <tr>
                <td style={{ ...cell, ...rowNo }}>1</td>
                {sh.groups.map(([label, span], i) => (
                  <td key={i} colSpan={span} style={{ ...cell, textAlign: 'center', fontWeight: 600,
                    background: 'var(--color-surface-2, #f0f3f7)', color: 'var(--color-text-secondary, #465062)' }}>{label}</td>
                ))}
              </tr>
            )}
            <tr>
              <td style={{ ...cell, ...rowNo }}>{sh.groups.length ? 2 : 1}</td>
              {sh.headings.map((h, i) => (
                <td key={i} style={{ ...cell, fontWeight: 600, background: 'var(--color-surface-2, #f0f3f7)',
                  borderBottom: '2px solid var(--color-text-secondary, #465062)' }}>{h}</td>
              ))}
            </tr>
            {sh.rows.map((r, ri) => (
              <tr key={ri}>
                <td style={{ ...cell, ...rowNo }}>{ri + (sh.groups.length ? 3 : 2)}</td>
                {r.map((v, ci) => (
                  <td key={ci} style={{ ...cell, background: tint(sh.parts[ci]),
                    textAlign: sh.numeric[ci] ? 'right' : 'left' }}>{v}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div role="tablist" aria-label="Sheets" style={{ display: 'flex', flexWrap: 'wrap', gap: 2, padding: '0 6px',
        border: `1px solid ${GRID}`, borderTop: 0, borderRadius: '0 0 6px 6px', background: 'var(--color-surface-2, #f4f6f9)' }}>
        {sheets.map((s, i) => (
          <button key={i} type="button" role="tab" aria-selected={i === idx} onClick={() => setActive(i)}
            style={{ border: 0, padding: '6px 14px', fontSize: 13, cursor: 'pointer', marginTop: -1,
              borderTop: `2px solid ${i === idx ? 'var(--color-success, #17703f)' : 'transparent'}`,
              background: i === idx ? 'var(--color-bg, #fff)' : 'transparent',
              color: i === idx ? 'var(--color-text, #1b2330)' : 'var(--color-text-secondary, #465062)',
              fontWeight: i === idx ? 600 : 400 }}>{s.name}</button>
        ))}
      </div>
      <div style={{ ...meta, display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 6 }}>
        <span>First {preview.records} record{preview.records === 1 ? '' : 's'} of the screen.</span>
        <span><Swatch color={TINT_H} /> Header columns</span>
        <span><Swatch color={TINT_D} /> Line columns</span>
        {format === 'csv' && sheets.length > 1 && <span>CSV: a .zip with one file per sheet.</span>}
      </div>
      {preview.missing.length > 0 && (
        <Note tone="warning">Left out because the screen no longer has them: {preview.missing.join(', ')}</Note>
      )}
    </div>
  )
}

const rowNo: CSSProperties = {
  background: 'var(--color-surface-2, #f4f6f9)', color: 'var(--color-text-faint, #687281)', textAlign: 'right',
  minWidth: 34, position: 'sticky', left: 0,
}

function Swatch({ color }: { color: string }) {
  return <i aria-hidden="true" style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2,
    background: color, border: `1px solid ${GRID}`, verticalAlign: -1, marginRight: 4 }} />
}
